import { toast } from 'react-toastify';
import { format } from '@formkit/tempo';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { createDataCatalog, IDataCatalog, IDataCatalogListing, VerdocsEndpoint } from '@verdocs/js-sdk';
import { useDataCatalogListing, useDataCatalogMarketplace, useDataCatalogs, useOrganization } from '../react-sdk/queries';
import { Button, SegmentedControl, Spinner } from '../Components';
import { AppState } from '../AppState';
import { URLS } from '../Routing/urls';
import { Dialog } from './Dialog';

const TYPE_LABELS = { text: 'Text', date: 'Date', boolean: 'Yes/No', choice: 'Choice' };
const PREVIEW_KEYS = 10;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map(word => word.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

const released = (listing: IDataCatalogListing) =>
  listing.released_at ? format(new Date(listing.released_at), 'MMM D, YYYY', 'en') : 'Not yet';

export function PublisherTile({ listing, large }: { listing: IDataCatalogListing; large?: boolean }) {
  const size = large ? 'size-12 rounded-[10px] text-[15px]' : 'size-9 rounded-lg text-[12px]';
  return listing.publisher.logo_url ? (
    <img
      src={listing.publisher.logo_url}
      alt=""
      className={`${size} shrink-0 bg-gray-50 object-contain`}
    />
  ) : (
    <div className={`${size} flex shrink-0 items-center justify-center bg-[#e4e0f8] font-semibold text-[#654dcb]`}>
      {initials(listing.publisher.name)}
    </div>
  );
}

interface DataCatalogMarketplaceDialogProps {
  onClose: () => void;
  onImported: (catalog: IDataCatalog) => void;
}

export function DataCatalogMarketplaceDialog({ onClose, onImported }: DataCatalogMarketplaceDialogProps) {
  const navigate = useNavigate();
  const organizationId = AppState.currentProfile?.organization_id || '';
  const [ search, setSearch ] = useState('');
  const [ q, setQ ] = useDebounceValue('', 250);
  const [ filter, setFilter ] = useState('all');
  const [ selectedId, setSelectedId ] = useState('');
  const [ importing, setImporting ] = useState(false);

  const orgQuery = useOrganization(organizationId);
  const catalogsQuery = useDataCatalogs(organizationId);
  const marketplaceQuery = useDataCatalogMarketplace(organizationId, q);
  const listings = marketplaceQuery.data || [];
  const shown = listings.filter(listing => filter === 'all' || listing.listing === filter);
  const selected = shown.find(listing => listing.id === selectedId) || shown[0];
  const previewQuery = useDataCatalogListing(organizationId, selected?.id || '');
  const preview = previewQuery.data?.id === selected?.id ? previewQuery.data : undefined;
  const copy = (catalogsQuery.data || []).find(catalog => catalog.source_catalog_id === selected?.id);

  const filters = [
    { key: 'all', label: `All ${listings.length}` },
    { key: 'shared', label: `Shared with you ${listings.filter(listing => listing.listing === 'shared').length}` },
    { key: 'public', label: `Public ${listings.filter(listing => listing.listing === 'public').length}` },
  ];

  const handleImport = () => {
    if (!selected || importing) {
      return;
    }

    setImporting(true);
    createDataCatalog(VerdocsEndpoint.getDefault(), organizationId, { from_catalog_id: selected.id })
      .then(catalog => {
        setImporting(false);
        toast.success(`Imported ${selected.name}`);
        onImported(catalog);
      })
      .catch((e: any) => {
        setImporting(false);
        toast.error(`Error importing the catalog: ${e.response?.data?.message || e.message}`);
      });
  };

  return (
    <Dialog
      onCancel={onClose}
      size="lg">
      <div className="-mx-5 -mt-4 flex items-start justify-between border-b border-[#ebecf0] px-5 pb-3 pt-4">
        <div>
          <div className="text-lg font-medium text-[#33364b]">Marketplace</div>
          <div className="mt-0.5 text-[13px] text-gray-500">
            Data catalogs you can import into {orgQuery.data?.name || 'your organization'}.
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close">
          <XMarkIcon className="size-5" />
        </button>
      </div>

      <div className="-mx-5 -mb-4 flex min-h-0 flex-1">
        <div className="flex w-[400px] shrink-0 flex-col border-r border-[#ebecf0]">
          <div className="flex flex-col gap-2.5 px-4 pb-2.5 pt-3.5">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setQ(e.target.value.trim());
                }}
                placeholder="Search by name, publisher, or key"
                className="h-9 w-full rounded-md border border-[#dcdee5] pl-9 pr-3 text-[13px] text-[#33364b] outline-none placeholder:text-gray-400 focus:border-[#654dcb]"
              />
            </div>
            <SegmentedControl
              options={filters}
              value={filter}
              onChange={setFilter}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
            {marketplaceQuery.isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner mode="dark" />
              </div>
            ) : shown.length === 0 ? (
              <div className="px-5 py-10 text-center text-[13px] text-gray-500">
                {q ? (
                  <>
                    <div className="mb-1 text-[14px] font-medium text-[#33364b]">No catalogs match &quot;{q}&quot;</div>
                    Try a publisher&apos;s name or a key such as applicant.full_name.
                  </>
                ) : (
                  <>
                    <div className="mb-1 text-[14px] font-medium text-[#33364b]">Nothing to import yet</div>
                    Catalogs appear here once an organization above yours shares one, or once one is approved for the public marketplace.
                  </>
                )}
              </div>
            ) : (
              shown.map(listing => (
                <button
                  key={listing.id}
                  onClick={() => setSelectedId(listing.id)}
                  className={`flex w-full gap-3 rounded-lg border px-2.5 py-2.5 text-left ${
                    listing.id === selected?.id ? 'border-[#d9d2fb] bg-[#f8f7fe]' : 'border-transparent hover:bg-[#fafafb]'
                  }`}>
                  <PublisherTile listing={listing} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[14px] font-medium text-[#33364b]">
                      {listing.name}
                      {listing.imported && (
                        <span className="rounded bg-[#e3f4ea] px-1.5 py-px text-[11px] font-medium text-[#2e9b64]">Imported</span>
                      )}
                    </div>
                    <div className="mt-px text-[12px] text-gray-500">{listing.publisher.name}</div>
                    {listing.description && (
                      <div className="mt-1 line-clamp-2 text-[12px] leading-snug text-gray-500">{listing.description}</div>
                    )}
                    <div className="mt-1 text-[11px] text-gray-400">
                      {listing.key_count} keys · Version {listing.version} · {released(listing)}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {selected && (
              <>
                <div className="flex items-start gap-3.5">
                  <PublisherTile
                    listing={selected}
                    large
                  />
                  <div>
                    <div className="text-[17px] font-medium text-[#33364b]">{selected.name}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-gray-500">
                      {selected.publisher.name} ·
                      <span className="rounded bg-[#e4e0f8] px-1.5 py-px text-[11px] font-medium text-[#654dcb]">
                        {selected.listing === 'public' ? 'Public' : 'Shared with you'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="my-4 flex gap-6 rounded-lg border border-[#ebecf0] bg-[#fafafb] px-3.5 py-3">
                  {[
                    [ 'Version', selected.version ],
                    [ 'Keys', selected.key_count ],
                    [ 'Released', released(selected) ],
                  ].map(([ label, value ]) => (
                    <div
                      key={label}
                      className="text-[12px] text-gray-500">
                      {label}
                      <div className="mt-0.5 text-[14px] font-medium text-[#33364b]">{value}</div>
                    </div>
                  ))}
                </div>

                {selected.description && <p className="mb-4 text-[13px] leading-relaxed text-[#33364b]">{selected.description}</p>}

                <div className="mb-2 flex items-baseline justify-between text-[13px] font-medium text-[#33364b]">
                  Keys
                  {preview && preview.entries.length > PREVIEW_KEYS && (
                    <span className="text-[12px] font-normal text-gray-500">
                      First {PREVIEW_KEYS} of {preview.entries.length}
                    </span>
                  )}
                </div>
                {preview ? (
                  <div className="overflow-hidden rounded-lg border border-[#ebecf0]">
                    <table className="w-full text-[13px]">
                      <thead className="border-b border-[#ebecf0] bg-[#fafafb]">
                        <tr className="text-left text-[12px] text-gray-500">
                          <th className="px-3 py-1.5 font-medium">Key</th>
                          <th className="px-3 py-1.5 font-medium">Label</th>
                          <th className="px-3 py-1.5 font-medium">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.entries.slice(0, PREVIEW_KEYS).map(entry => (
                          <tr
                            key={entry.key}
                            className="border-t border-[#f2f3f5] first:border-0">
                            <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[12px] text-[#4b3fa0]">{entry.key}</td>
                            <td className="px-3 py-1.5">{entry.label}</td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-[12px] text-gray-500">{TYPE_LABELS[entry.type]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex justify-center py-6">
                    <Spinner mode="dark" />
                  </div>
                )}
                {preview && preview.entries.length > PREVIEW_KEYS && (
                  <div className="pt-2 text-[12px] text-gray-500">and {preview.entries.length - PREVIEW_KEYS} more</div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-[#ebecf0] px-6 py-3">
            <div className="text-[12px] leading-snug text-gray-500">
              {copy ? (
                <>
                  You already have a copy.{' '}
                  <button
                    className="text-[#654dcb] hover:underline"
                    onClick={() => navigate(URLS.SETTINGS_DATA_CATALOG.build(copy.id))}>
                    Open your copy
                  </button>
                </>
              ) : (
                selected && 'Your copy can be edited. New versions arrive as updates you review.'
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {importing && <Spinner mode="dark" />}
              <Button
                size="small"
                variant="secondary"
                label="Cancel"
                onClick={onClose}
                disabled={importing}
              />
              <Button
                size="small"
                label={copy ? 'Imported' : 'Import a copy'}
                onClick={handleImport}
                disabled={!selected || !!copy || importing}
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
